import { useState } from 'react'
import { Link } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { appAuthApi, type Device } from '../api'
import { t } from '../i18n'
import { Button, Notice, ago } from '../components/ui'

/*
 * Account → Devices & apps (docs/bridge-auth.md §5): the signed-in apps, one
 * Sign out each and Sign out everywhere. A signed-out app stops at its next
 * request (its access token is revoked with the grant).
 */

const KEY = ['me', 'devices']
const date = (iso: string) => new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })

function Row({ d }: { d: Device }) {
  const qc = useQueryClient()
  const out = useMutation({
    mutationFn: () => appAuthApi.revoke(d.id),
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  })
  const meta = [
    d.app_version && t('devices.version', { v: d.app_version }),
    d.last_used_at ? t('devices.lastUsed', { when: ago(d.last_used_at) }) : t('devices.neverUsed'),
    d.last_ip && t('devices.from', { ip: d.last_ip }),
    t('devices.since', { date: date(d.created_at) }),
  ].filter(Boolean)
  return (
    <li className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-b border-line py-4 last:border-b-0" data-testid="device-row">
      <div className="min-w-0">
        <p className="truncate font-medium text-text">{d.name}</p>
        <p className="text-sm text-muted">{meta.join(' · ')}</p>
      </div>
      <Button variant="danger" disabled={out.isPending} onClick={() => out.mutate()} aria-label={`${t('devices.signOut')}: ${d.name}`}>
        {out.isPending ? t('devices.signingOut') : t('devices.signOut')}
      </Button>
    </li>
  )
}

export function DevicesPage() {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: KEY, queryFn: appAuthApi.devices })
  const [ask, setAsk] = useState(false)
  const [done, setDone] = useState<number | null>(null)
  const all = useMutation({
    mutationFn: appAuthApi.revokeAll,
    onSuccess: (r) => setDone(r.revoked),
    onSettled: () => {
      setAsk(false)
      return qc.invalidateQueries({ queryKey: KEY })
    },
  })
  const list = q.data?.devices ?? []

  return (
    <div data-testid="devices-page" className="flex max-w-3xl flex-col gap-6">
      <div className="border-b border-line pb-5">
        <h1 className="m-0 text-ui-heading text-text">{t('devices.title')}</h1>
        <p className="mt-2 mb-0 text-ui-body text-text-soft">{t('devices.intro')}</p>
      </div>
      {done !== null && <Notice kind="info">{t('devices.done', { n: done })}</Notice>}
      {q.isPending ? (
        <p className="text-sm text-muted">{t('appAuth.loading')}</p>
      ) : q.isError ? (
        <div className="flex flex-wrap items-center gap-3">
          <Notice>{t('devices.failed')}</Notice>
          <Button onClick={() => q.refetch()}>{t('devices.retry')}</Button>
        </div>
      ) : list.length === 0 ? (
        <p className="text-sm text-muted" data-testid="devices-empty">
          {t('devices.empty')}{' '}
          <Link to="/apps" className="text-text underline underline-offset-4">
            {t('devices.getApps')}
          </Link>
        </p>
      ) : (
        <>
          <ul className="rounded-[0.5rem] border border-line bg-panel px-4">
            {list.map((d) => (
              <Row key={d.id} d={d} />
            ))}
          </ul>
          {list.length > 1 &&
            (ask ? (
              <div className="flex flex-wrap items-center gap-3 rounded-md border border-fail/50 px-3 py-2" role="group" aria-label={t('devices.everywhere')}>
                <span className="text-sm text-text">{t('devices.everywhereAsk', { n: list.length })}</span>
                <Button variant="danger" disabled={all.isPending} onClick={() => all.mutate()}>
                  {t('devices.everywhereYes')}
                </Button>
                <Button variant="quiet" onClick={() => setAsk(false)}>
                  {t('appAuth.cancel')}
                </Button>
              </div>
            ) : (
              <div>
                <Button variant="danger" onClick={() => setAsk(true)}>
                  {t('devices.everywhere')}
                </Button>
              </div>
            ))}
        </>
      )}
    </div>
  )
}
